import { hasKey } from '@/utils/resultsHelper';
import { datasetUrl, splitUrlArguments } from '@/constants/urls';
import { datasetManagePermissions, datasetPermissions } from './sharing/constants';
import validateDictionaries, {
  dictionariesDiff,
  removeUUIDsFromDictionaries,
  sanitizedDictionaries,
} from './dictionaries/helper';
import { catalogueDiff, validateCatalogue } from './catalogue/helper';
import { datasetSourceEntities, datasetCatalogueSteps, datasetDictionariesStep } from './constants';
import { transformDictionaries } from './dictionaries/state/state';
import { allowedDatasetStepIndexes, datasetStepsIncludes, isDatasetWizardOn } from './state/state';
import AttachmentsPolicy from './attachments/policies/attachmentsPolicy';

const datasetDiff = ({ catalogue, dictionaries, copy }) => {
  const diff = Object.keys(copy).length > 0 ? {
    catalogue: catalogueDiff(catalogue, copy.catalogue),
    ...dictionariesDiff(transformDictionaries(dictionaries), copy.dictionaries),
  } : {};

  Object.keys(diff).forEach((k) => diff[k] == null && delete diff[k]);

  return Object.keys(diff).length > 0 ? diff : null;
};

export const sanitizedDataset = (dataset) => {
  let updates = { ...dataset };

  if (dataset.dictionaries) {
    const dictionariesWithoutUUIDs = removeUUIDsFromDictionaries(dataset.dictionaries);
    updates = { ...updates, dictionaries: sanitizedDictionaries(dictionariesWithoutUUIDs) };
  }

  return updates;
};

export const canEditDataset = ({ permissions, isNew }, role) => {
  const { datasets } = role.permissions;
  const perm = isNew ? datasets.create : datasets.update;

  return perm && permissions[datasetPermissions.UPDATE];
};

export const validateDataset = (dataset, diff, template) => {
  const catalogueErrors = hasKey(diff, 'catalogue') ? validateCatalogue(diff.catalogue, template) : [];
  const dictionaryErrors = hasKey(diff, 'dictionaries') ? validateDictionaries(diff, dataset) : [];

  return catalogueErrors.concat(dictionaryErrors);
};

export const datasetSourcePartsFromUrl = (url) => {
  const [, type, datasetSourceCode, action] = splitUrlArguments(url);
  const hasSourceType = action === datasetSourceEntities.DATASET
      && Object.values(datasetSourceEntities).includes(type.slice(0, -1));

  return {
    hasSourceType,
    isNew: hasSourceType || type === datasetSourceEntities.DATASET,
    datasetSourceCode,
    datasetSourceType: hasSourceType ? type.slice(0, -1) : datasetSourceEntities.DATASET,
  };
};

export const isNewDataset = (dataset, url) => {
  const isNewExists = hasKey(dataset, 'isNew');

  return ({
    isNew: isNewExists ? dataset.isNew : datasetSourcePartsFromUrl(url).isNew,
    datasetUrl: isNewExists && !dataset.isNew ? datasetUrl(dataset) : url,
  });
};

export const datasetSourceFromUrl = (url) => {
  const { datasetSourceCode, hasSourceType, datasetSourceType } = datasetSourcePartsFromUrl(url);
  return { datasetSourceCode: hasSourceType ? datasetSourceCode : null, datasetSourceType };
};

export const datasetIsEditing = ({ isEditing, asViewer, steps }) => (
  (asViewer ? !asViewer.on : true) && (isEditing || (steps && steps.wizardOn
      && datasetStepsIncludes({ ...datasetCatalogueSteps, ...datasetDictionariesStep }, steps.activeStepIndex)))
);

export const compactStepIndexes = ({ permissions, steps }, { sections }, allowAttachments) => {
  const indexes = allowedDatasetStepIndexes({ permissions }, { sections }, allowAttachments);
  const currentIndex = indexes.indexOf(steps.activeStepIndex);
  const { length } = indexes;

  if (length <= 3) { return indexes; }
  if (currentIndex <= 0) { return [indexes[0], indexes[1], indexes[2]]; }
  if (currentIndex >= length - 1) { return [indexes[length - 3], indexes[length - 2], indexes[length - 1]]; }
  return [indexes[currentIndex - 1], indexes[currentIndex], indexes[currentIndex + 1]];
};

export const checkDatasetIsNew = (dataset, exists, url) => {
  if (exists) { return isNewDataset(dataset, url); }
  return { ...datasetSourcePartsFromUrl(url), datasetUrl: url };
};

export const datasetWizardChecks = (dataset, exists, wizardOff, role, { url }) => {
  const ds = {
    ...checkDatasetIsNew(dataset, exists, url),
    asViewer: dataset.asViewer,
    attachments: dataset.attachments,
    isEditing: dataset.isEditing,
    permissions: dataset.permissions,
    status: dataset.status,
    steps: { wizardOn: dataset.steps.wizardOn },
  };

  if (!exists && ds.isNew) { ds.steps.wizardOn = !wizardOff; }
  const policy = new AttachmentsPolicy(role, ds);

  return {
    canReadAttachments: policy.canRead,
    canUploadAttachments: exists && policy.canUpload,
    datasetUrl: ds.datasetUrl,
    exists,
    isNew: ds.isNew,
    wizardOn: isDatasetWizardOn(ds, role),
  };
};

export const canChangeDatasetFoundations = (dataset, permissions) => {
  const { create, update } = permissions.datasets;
  const allow = dataset.isNew ? create : update;
  const allowed = {
    canChangeTemplate: allow && dataset.steps?.sourceType !== datasetSourceEntities.TEMPLATE,
    canChangeDatasource: allow,
  };

  if (!dataset.isNew) {
    allowed.canChangeTemplate = allow && dataset.permissions[datasetManagePermissions.SETTINGS];
    allowed.canChangeDatasource = allow && dataset.permissions[datasetManagePermissions.DATASOURCE];
  }

  return allowed;
};

export default datasetDiff;
